import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import clsx from "clsx";
import { useFormik } from "formik";
import { AxiosError } from "axios";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import * as auth from "../redux/AuthRedux";
import { enrollTwoFactor, verifyTwoFactorEnrollment } from "../redux/AuthCRUD";
import { ErrorResponse } from "../../../support/utils";

const enrollSchema = Yup.object().shape({
	otp: Yup.string().required("OTP is required"),
});

type EnrollState = { password?: string };

/**
 * Forced TOTP enrolment for a freshly approved account. Reached from the login
 * screen when the API returns status "ENROLL_2FA". The password is passed in
 * router state (in-memory only) so we can re-authenticate the enrol/verify
 * calls without storing it anywhere.
 */
export function EnrollTwoFactor() {
	const { email = "" } = useParams<{ email: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const password = (location.state as EnrollState | null)?.password;

	const [loading, setLoading] = useState(false);
	const [qrCode, setQrCode] = useState<string | null>(null);
	const [secret, setSecret] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loadQrCode = useCallback(async () => {
		if (!password) {
			return;
		}
		try {
			const { data } = await enrollTwoFactor(email, password);
			setQrCode(data.value.qr_code_inline);
			setSecret(data.value.secret);
		} catch (err) {
			const axiosErr = err as AxiosError<ErrorResponse>;
			setError(
				axiosErr.response?.data?.message ??
					"Could not start two-factor enrolment. Please log in again."
			);
		}
	}, [email, password]);

	useEffect(() => {
		loadQrCode();
	}, [loadQrCode]);

	const handleCopySecret = async () => {
		if (!secret) {
			return;
		}
		try {
			await navigator.clipboard.writeText(secret);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard API unavailable (e.g. non-HTTPS) — the secret is still
			// shown for manual copying.
		}
	};

	const formik = useFormik({
		initialValues: { otp: "" },
		validationSchema: enrollSchema,
		onSubmit: async (values, { setStatus, setSubmitting }) => {
			if (!password) {
				return;
			}
			setSubmitting(true);
			setLoading(true);
			try {
				const { data } = await verifyTwoFactorEnrollment(
					email,
					password,
					values.otp
				);
				setLoading(false);
				setSubmitting(false);
				dispatch(auth.actions.login(data.access_token));
			} catch (err) {
				const axiosErr = err as AxiosError<ErrorResponse>;
				setLoading(false);
				setSubmitting(false);
				setStatus(
					axiosErr.response?.data?.message ??
						"Invalid OTP, please try again."
				);
			}
		},
	});

	// Without the password (e.g. page refresh / direct navigation) enrolment
	// cannot proceed — send the user back to the login screen.
	if (!password) {
		return (
			<div className="w-100">
				<div className="pb-5 pb-lg-10">
					<h3 className="fw-bolder text-dark display-6">
						Two-factor setup
					</h3>
				</div>
				<div className="mb-lg-15 alert alert-warning">
					<div className="alert-text">
						Your session expired. Please log in again to set up
						two-factor authentication.
					</div>
				</div>
				<button
					type="button"
					onClick={() => navigate("/auth/login")}
					className="btn btn-primary fw-bolder fs-6 px-8 py-4 my-3"
				>
					Back to login
				</button>
			</div>
		);
	}

	return (
		<form
			className="form w-100"
			onSubmit={formik.handleSubmit}
			noValidate
			id="kt_login_enroll_2fa_form"
		>
			<div className="pb-lg-10 pb-5">
				<h3 className="fw-bolder text-dark display-6">
					Set up two-factor authentication
				</h3>
				<p className="text-muted fw-bold fs-5">
					Scan the QR code with an authenticator app, then enter the
					6-digit code to finish. Tip: store the secret in your
					password manager (e.g. KeyHub).
				</p>
			</div>

			{error && (
				<div className="mb-lg-15 alert alert-danger">
					<div className="alert-text font-weight-bold">{error}</div>
				</div>
			)}

			{formik.status && (
				<div className="mb-lg-15 alert alert-danger">
					<div className="alert-text font-weight-bold">
						{formik.status}
					</div>
				</div>
			)}

			{qrCode && (
				<div className="d-flex justify-content-center mb-6">
					{qrCode.trim().startsWith("<") ? (
						<div
							// pragmarx returns inline SVG markup
							dangerouslySetInnerHTML={{ __html: qrCode }}
						/>
					) : (
						<img src={qrCode} alt="Two-factor QR code" />
					)}
				</div>
			)}

			{secret && (
				<div className="mb-10">
					<label className="form-label fs-7 fw-bold text-muted">
						Can't scan? Enter this key manually:
					</label>
					<div className="input-group input-group-lg input-group-solid">
						<input
							type="text"
							readOnly
							className="form-control form-control-lg form-control-solid fw-bold"
							value={secret}
							onFocus={(e) => e.target.select()}
						/>
						<button
							type="button"
							className="btn btn-light-primary fw-bolder"
							onClick={handleCopySecret}
						>
							{copied ? "Copied!" : "Copy"}
						</button>
					</div>
				</div>
			)}

			{/* begin::OTP */}
			<div className="fv-row mb-10">
				<label className="form-label fs-6 fw-bolder text-dark">
					Authentication code
				</label>
				<input
					placeholder="6-digit code"
					type="text"
					autoComplete="one-time-code"
					{...formik.getFieldProps("otp")}
					className={clsx(
						"form-control form-control-lg form-control-solid",
						{
							"is-invalid":
								formik.touched.otp && formik.errors.otp,
						},
						{
							"is-valid":
								formik.touched.otp && !formik.errors.otp,
						}
					)}
				/>
				{formik.touched.otp && formik.errors.otp && (
					<div className="fv-plugins-message-container">
						<div className="fv-help-block">{formik.errors.otp}</div>
					</div>
				)}
			</div>
			{/* end::OTP */}

			<div className="d-flex flex-wrap pb-lg-0 pb-5">
				<button
					type="submit"
					id="kt_login_enroll_2fa_submit_button"
					className="btn btn-primary fw-bolder fs-6 px-8 py-4 my-3 me-3"
					disabled={formik.isSubmitting || !formik.isValid || !qrCode}
				>
					{!loading && (
						<span className="indicator-label">
							Verify &amp; activate
						</span>
					)}
					{loading && (
						<span
							className="indicator-progress"
							style={{ display: "block" }}
						>
							Please wait...{" "}
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					)}
				</button>
				<Link to="/auth/login">
					<button
						type="button"
						className="btn btn-light-primary fw-bolder fs-6 px-8 py-4 my-3"
					>
						Cancel
					</button>
				</Link>
			</div>
		</form>
	);
}
